import { Nullable } from "common/types";
import { ProductRecord } from "common/records/Product";
import { RootState } from "common/reducers";
import { getRecordsByModel } from "common/selectors/idsManagement.selectors";
import { createSelector } from "reselect";
import { MeasurementLinkRecord } from "common/records/MeasurementLink";
import { productOverviewById } from "common/selectors/product.selectors";

/**
 * Loads the byId map for measurementLinks
 *
 * @param {RootState} state The RootState
 * @returns {Map<string | number, MeasurementLinkRecord>} The byId map
 */
export const byId = (state: RootState) => state.getIn(["measurementLinks", "byId"]);

/**
 * Loads a specific measurementLink by its ID
 *
 * @param {RootState} state The RootState
 * @param {Object} props The properties object
 * @param {number} props.measurementLinkId The ID of the measurementLink
 * @returns {MeasurementLinkRecord} The measurementLink record
 */
export const measurementLink = createSelector(
  [byId, (state, props: { measurementLinkId: number }) => props],
  (byId, props) => byId.get(props.measurementLinkId),
);

/**
 * Loads all measurementLinks using getRecordsByModel
 *
 * @param {RootState} state The RootState
 * @returns {List<MeasurementLinkRecord>} List of measurementLink records
 */
export const measurementLinks = (state: RootState) =>
  getRecordsByModel(state, { modelName: "measurementLinks", path: ["measurementLinks", "byId"] });

/**
 * Loads the measurementLink associated with a product, traversing up the product hierarchy if necessary
 *
 * @param {RootState} state The RootState
 * @param {Object} props The properties object
 * @param {Nullable<ProductRecord>} props.product The product record
 * @returns {Nullable<MeasurementLinkRecord>} The measurementLink record or null if not found
 */
export const productMeasurementLink = createSelector(
  [measurementLinks, (state, props: { product: Nullable<ProductRecord> }) => ({ state, props })],
  (measurementLinks, { state, props }) => {
    const { product } = props;
    if (!product) return null;

    let currentProduct: Nullable<ProductRecord> = product;
    let matchingMeasurementLink: Nullable<MeasurementLinkRecord> = null;

    while (currentProduct && !matchingMeasurementLink) {
      matchingMeasurementLink = measurementLinks.find(
        (measurementLink) => measurementLink.item_uuid === currentProduct?.uuid,
      );
      if (!matchingMeasurementLink) {
        currentProduct = currentProduct.parent_id ? productOverviewById(state, currentProduct.parent_id) : null;
      }
    }

    if (matchingMeasurementLink && currentProduct?.uuid !== product.uuid) {
      matchingMeasurementLink = matchingMeasurementLink.set("inherited", true);
    }

    return matchingMeasurementLink;
  },
);
